import React, { Ref, forwardRef, useEffect, useState } from 'react';
import css from './DatePicker.module.css';
import { DateUtility } from '@mchs/web-core';

export interface DatePickerProps {
  value?: Date | null;
  placeholder?: string;
  disabled?: boolean;
  onFocus: () => void;
  onBlur: () => void;
  onDateChange?: (value: Date | null | undefined) => void;
}

const DatePickerComponent: React.ForwardRefRenderFunction<
  HTMLInputElement,
  DatePickerProps
> = (props: DatePickerProps, ref: Ref<HTMLInputElement>) => {
  const { disabled = false } = props;
  const [valueStr, setValueStr] = useState<string>(
    props.value && props.value instanceof Date
      ? DateUtility.formatDateISOYearMonthDay(props.value)
      : ''
  );

  useEffect(() => {
    setValueStr(
      props.value && props.value instanceof Date
        ? DateUtility.formatDateISOYearMonthDay(props.value)
        : ''
    );
  }, [props.value]);

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    if (newValue === '' || newValue === undefined || newValue === null) {
      props.onDateChange && props.onDateChange(null);
    } else if (newValue.startsWith('0')) {
      // the user must be modifying the year
      // if we fire a date change here, we end up
      // updating the value string of the input
      // so the next time the user enters a key,
      // it will overwrite the first key
      // i.e. when typing 2023, you get the following updates
      // (if updating the date... which triggers useEffect):
      //  0002
      //  0000
      //  0002
      //  0003
    } else {
      const newValueAsDate = DateUtility.parseDateISOYearMonthDay(newValue);
      if (newValueAsDate !== null) {
        props.onDateChange && props.onDateChange(newValueAsDate);
      }
    }

    setValueStr(newValue);
  };

  return (
    <input
      ref={ref}
      type="date"
      role="textbox"
      value={valueStr}
      disabled={disabled}
      className={css.datePicker}
      placeholder={props.placeholder}
      autoCapitalize="off"
      autoComplete="off"
      onChange={onChange}
      onFocus={props.onFocus}
      onBlur={props.onBlur}
    />
  );
};

const DatePicker = forwardRef(DatePickerComponent);

export default DatePicker;
